import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import { MailIcon, PhoneIcon, LocationIcon, CheckIcon, ClockIcon } from '../components/Icons';
import { companyInfo } from '../data';
import { getPageMeta } from '../utils/seoHelmet';

const Contact = () => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
    const formRef = useRef(null);

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        company: '',
        jobTitle: '',
        phone: '',
        interest: '',
        message: ''
    });

    // Scroll animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsFormVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        if (formRef.current) observer.observe(formRef.current);
        return () => observer.disconnect();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setFormData({
                    fullName: '',
                    email: '',
                    company: '',
                    jobTitle: '',
                    phone: '',
                    interest: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const meta = getPageMeta('contact');

    return (
        <main>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <meta name="keywords" content={meta.keywords} />
                <meta name="robots" content={meta.robots} />
                <link rel="canonical" href={meta.canonical} />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.ogImage} />
                <meta property="og:url" content={meta.canonical} />
            </Helmet>
            {/* Page Hero */}
            <section 
                className="page-hero page-hero--dark" 
                style={{ 
                    backgroundImage: `linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(15, 15, 35, 0.95)), url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=600&fit=crop&q=80')` 
                }}
            >
                <div className="container">
                    <span className="page-hero__eyebrow">Get In Touch</span>
                    <h1 className="page-hero__title">Contact Us</h1>
                    <p className="page-hero__subtitle">
                        Ready to discuss how Phoenix AI can support your organization? 
                        We're here to answer your questions.
                    </p>
                </div>
            </section>

            {/* Contact Section */}
            <section 
                className={`section contact-page ${isFormVisible ? 'contact-page--visible' : ''}`}
                ref={formRef}
            >
                <div className="contact-page__bg"></div>
                <div className="container">
                    <div className="contact-container">
                        {/* Contact Information */}
                        <div className="contact-info">
                            <div className="contact-info__header">
                                <span className="contact-info__eyebrow">Contact Information</span>
                                <h2 className="contact-info__title">Get in Touch</h2>
                                <p className="contact-info__text">
                                    Our enterprise team is available to discuss your requirements 
                                    and provide detailed information about our solutions.
                                </p>
                            </div>

                            <div className="contact-info__items">
                                <div className="contact-info__item">
                                    <div className="contact-info__icon">
                                        <MailIcon size={20} />
                                    </div>
                                    <div className="contact-info__details">
                                        <p className="contact-info__label">Email</p>
                                        <p className="contact-info__value">{companyInfo.email}</p>
                                    </div>
                                </div>

                                <div className="contact-info__item">
                                    <div className="contact-info__icon">
                                        <PhoneIcon size={20} />
                                    </div>
                                    <div className="contact-info__details">
                                        <p className="contact-info__label">Phone</p>
                                        <p className="contact-info__value">{companyInfo.phone}</p>
                                    </div>
                                </div>

                                <div className="contact-info__item">
                                    <div className="contact-info__icon">
                                        <LocationIcon size={20} />
                                    </div>
                                    <div className="contact-info__details">
                                        <p className="contact-info__label">Office</p>
                                        <p className="contact-info__value">
                                            {companyInfo.address.line1}<br />
                                            {companyInfo.address.line2}<br />
                                            {companyInfo.address.line3}<br />
                                            {companyInfo.address.postcode}, {companyInfo.address.city}
                                        </p>
                                    </div>
                                </div>

                                <div className="contact-info__item">
                                    <div className="contact-info__icon">
                                        <ClockIcon size={20} />
                                    </div>
                                    <div className="contact-info__details">
                                        <p className="contact-info__label">Business Hours</p>
                                        <p className="contact-info__value">
                                            {companyInfo.businessHours.weekdays}<br />
                                            {companyInfo.businessHours.weekend}<br />
                                            <span style={{ fontSize: '13px', color: '#8a8f99' }}>{companyInfo.businessHours.timezone}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-info__enterprise">
                                <div className="contact-info__enterprise-icon">
                                    <CheckIcon size={16} />
                                </div>
                                <div>
                                    <p className="contact-info__enterprise-title">Enterprise Inquiries</p>
                                    <p className="contact-info__enterprise-text">
                                        For enterprise pricing, custom solutions, or partnership opportunities, 
                                        please complete the form and our dedicated team will respond within 24 hours.
                                    </p>
                                </div>
                            </div>

                            {/* Map Section */}
                            <div className="contact-info__map">
                                <p className="contact-info__map-label">Our Location</p>
                                <div className="contact-info__map-container">
                                    <iframe
                                        src={companyInfo.mapEmbedUrl}
                                        title="Phoenix AI Office Location"
                                        className="contact-info__map-iframe"
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    ></iframe>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="contact-form-section">
                            <div className="contact-form-section__header">
                                <span className="contact-form-section__eyebrow">Send a Message</span>
                                <h2 className="contact-form-section__title">Start a Conversation</h2>
                                <p className="contact-form-section__subtitle">
                                    Complete the form below and we'll be in touch shortly.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="contact-form">
                                <div className="form-group">
                                    <label htmlFor="fullName" className="form-label">Full Name *</label>
                                    <input 
                                        type="text" 
                                        id="fullName" 
                                        name="fullName"
                                        className="form-input"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        required 
                                    />
                                </div>

                                <div className="contact-form__row">
                                    <div className="form-group">
                                        <label htmlFor="email" className="form-label">Business Email *</label>
                                        <input 
                                            type="email" 
                                            id="email" 
                                            name="email"
                                            className="form-input"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone" className="form-label">Phone Number</label>
                                        <input 
                                            type="tel" 
                                            id="phone" 
                                            name="phone"
                                            className="form-input"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="contact-form__row">
                                    <div className="form-group">
                                        <label htmlFor="company" className="form-label">Company *</label>
                                        <input 
                                            type="text" 
                                            id="company" 
                                            name="company"
                                            className="form-input"
                                            value={formData.company}
                                            onChange={handleChange}
                                            required 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="jobTitle" className="form-label">Job Title</label>
                                        <input 
                                            type="text" 
                                            id="jobTitle" 
                                            name="jobTitle"
                                            className="form-input"
                                            value={formData.jobTitle}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="interest" className="form-label">Area of Interest</label>
                                    <select 
                                        id="interest" 
                                        name="interest"
                                        className="form-input"
                                        value={formData.interest}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select an option</option>
                                        <option value="training">AI Software Training</option>
                                        <option value="compliance">Compliance & Risk Consulting</option>
                                        <option value="cctv">AI-Powered CCTV Platform</option>
                                        <option value="products">Products & Platforms</option>
                                        <option value="partnership">Partnership Opportunities</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message" className="form-label">Message *</label>
                                    <textarea 
                                        id="message" 
                                        name="message"
                                        className="form-textarea"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Please describe your requirements or questions..."
                                        required
                                    ></textarea>
                                </div>

                                <Button type="submit" fullWidth size="large" disabled={isSubmitting}>
                                    {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                                </Button>

                                {submitStatus === 'success' && (
                                    <div className="contact-form__status contact-form__status--success">
                                        <CheckIcon size={16} />
                                        Thank you for your inquiry. Our team will be in touch shortly.
                                    </div>
                                )}

                                {submitStatus === 'error' && (
                                    <div className="contact-form__status contact-form__status--error">
                                        Something went wrong. Please try again or contact us directly.
                                    </div>
                                )}

                                <p className="contact-form__disclaimer">
                                    By submitting this form, you agree to our privacy policy 
                                    and consent to being contacted regarding your inquiry.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Contact;
